/**
 * 处理动态模块加载失败的问题
 * 特别是在使用浏览器返回键时，由于 Vite HMR 和 BFCache 的冲突导致的模块加载失败
 *
 * 策略：
 * 1. 检测 BFCache 恢复并刷新
 * 2. 捕获 error / unhandledrejection 事件
 * 3. 直接监听 Vue Router 导航错误
 * 4. 防止无限重载（最多 3 次）
 */
export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.client) return

  // 防止重复重载 + 防止无限循环
  let isReloading = false

  const RELOAD_KEY = '__nuxt_module_reload_count'
  const MAX_RELOADS = 3

  const canReload = (): boolean => {
    const count = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10)
    if (count >= MAX_RELOADS) {
      console.warn(`⛔ Max reloads (${MAX_RELOADS}) reached, giving up`)
      return false
    }
    return true
  }

  const performReload = (source: string) => {
    if (isReloading) {
      console.log(`Already reloading, skipping ${source}`)
      return
    }
    if (!canReload()) return

    isReloading = true
    const count = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10) + 1
    sessionStorage.setItem(RELOAD_KEY, String(count))

    console.log(`🔄 [${source}] Reloading page (${count}/${MAX_RELOADS}) to recover from module loading error...`)
    window.location.reload()
  }

  // 检测是否从 BFCache 恢复
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      console.log('📦 Page restored from BFCache, reloading to prevent stale modules')
      performReload('BFCache')
    }
  })

  // 捕获动态导入错误（error 事件）
  window.addEventListener('error', (event) => {
    const error = event.error || event.message
    const errorString = String(error)

    if (errorString.includes('Failed to fetch dynamically imported module') ||
        errorString.includes('Failed to load module script') ||
        errorString.includes('Importing a module script failed')) {

      console.warn('⚠️ Module loading failed (error event):', errorString)
      event.preventDefault()
      performReload('error event')
    }
  })

  // 捕获未处理的 Promise rejection
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const reasonString = String(reason)

    if (reasonString.includes('Failed to fetch dynamically imported module') ||
        reasonString.includes('Failed to load module script') ||
        reasonString.includes('H3Error: Failed to fetch dynamically imported module')) {

      console.warn('⚠️ Module loading failed (unhandledrejection):', reasonString)
      event.preventDefault()
      performReload('unhandledrejection')
    }
  }, true) // 使用捕获阶段，优先处理

  // 直接监听 Vue Router 导航错误
  // 这是兜底：如果 error 和 unhandledrejection 都没捕获到（因为 Vue Router 内部消费了错误）
  const router = useRouter()
  router.onError((error) => {
    const errorString = String(error)
    if (errorString.includes('Failed to fetch dynamically imported module') ||
        errorString.includes('Failed to load module script') ||
        errorString.includes('MIME type')) {
      console.warn('⚠️ Router navigation error (module load):', errorString)
      performReload('router.onError')
    }
  })
})
